'use client';

import { useState } from 'react';
import { CHANNEL_CATEGORIES } from '@/lib/categories';
import { buildYoutubeThumbnail, buildYoutubeThumbnailFallback } from '@/lib/youtube';

interface Channel {
  ref: string;
  name: string;
  nickname?: string | null;
  live_url: string | null;
  live_active?: number;
  channel_title?: string | null;
  channel_image?: string | null;
  category1?: string | null;
  category2?: string | null;
  adult_only?: number;
}

const NOTICE_TITLE = 'LIVESOHO 서비스 이용약관 개정 사전 안내';
const NOTICE_BODY = [
  '안녕하세요, LIVESOHO입니다.',
  '회원님께 더 안전하고 편리한 서비스를 제공하기 위해 서비스 이용약관 일부를 아래와 같이 개정할 예정임을 안내드립니다.',
  '주요 개정 내용\n1) 개인정보 처리방침 세부 조항 정비\n2) 결제·환불 정책 명확화\n3) 라이브 커머스 채널 운영 정책 신설',
  '공고일: 2026년 8월 3일\n시행일: 2026년 8월 17일',
  '개정된 약관은 시행일부터 적용되며, 시행일 이전에 별도의 의사표시가 없으실 경우 개정에 동의하신 것으로 간주됩니다. 자세한 내용은 고객센터로 문의해 주세요.',
];

// 방송중이 아닌 채널 카드 배경 — ref 기준 고정 해시로 매번 같은 색이 나오게 한다(새로고침마다 안 바뀜).
const CARD_COLORS = ['bg-blue-200', 'bg-pink-200', 'bg-emerald-200', 'bg-amber-200', 'bg-violet-200', 'bg-rose-200', 'bg-cyan-200', 'bg-lime-200'];
function pickCardColor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return CARD_COLORS[hash % CARD_COLORS.length];
}

export default function ChannelList({ channels }: { channels: Channel[] }) {
  const [liveOnly, setLiveOnly] = useState(false);
  const [category, setCategory] = useState<string>('전체');
  const [noticeOpen, setNoticeOpen] = useState(false);

  const visible = channels
    .filter(c => !liveOnly || !!c.live_active)
    .filter(c => category === '전체' || c.category1 === category || c.category2 === category);

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1 flex-1 min-w-0" style={{ scrollbarWidth: 'none' }}>
          {['전체', ...CHANNEL_CATEGORIES].map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`shrink-0 cursor-pointer text-[10px] font-medium rounded-full px-1.5 py-0.5 border whitespace-nowrap ${
                category === c ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1 shrink-0 cursor-pointer select-none">
          <span className="text-[10px] text-gray-500 whitespace-nowrap">방송중</span>
          <button
            type="button"
            role="switch"
            aria-checked={liveOnly}
            onClick={() => setLiveOnly(v => !v)}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${liveOnly ? 'bg-red-500' : 'bg-gray-300'}`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${liveOnly ? 'translate-x-4' : 'translate-x-1'}`}
            />
          </button>
        </label>
      </div>

      {/* 공지사항 */}
      <button
        type="button"
        onClick={() => setNoticeOpen(true)}
        className="w-full flex items-center gap-2.5 bg-white border border-gray-200 rounded-lg px-3 py-2.5 mb-2.5 text-xs shadow-xs text-left cursor-pointer hover:bg-gray-50"
      >
        <span className="shrink-0 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
          공지
        </span>
        <span className="truncate text-xs font-medium text-gray-700">
          {NOTICE_TITLE}
        </span>
      </button>

      {noticeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5"
          onClick={() => setNoticeOpen(false)}
        >
          <div
            className="w-full max-w-sm max-h-[80vh] overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <h2 className="text-base font-bold text-black">{NOTICE_TITLE}</h2>
              <button type="button" onClick={() => setNoticeOpen(false)}
                aria-label="닫기"
                className="shrink-0 text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>
            <div className="space-y-3 text-xs text-gray-600 leading-relaxed">
              {NOTICE_BODY.map((p, i) => (
                <p key={i} className="whitespace-pre-line">{p}</p>
              ))}
            </div>
            <button type="button" onClick={() => setNoticeOpen(false)}
              className="w-full mt-5 bg-gray-800 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-900">
              확인
            </button>
          </div>
        </div>
      )}

      {channels.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-8">아직 개설된 채널이 없습니다.</p>
      ) : visible.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-8">해당하는 채널이 없습니다.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2.5">
          {visible.map(c => {
            const thumb = c.live_active ? buildYoutubeThumbnail(c.live_url ?? '') : null;
            const label = c.channel_title || c.nickname || c.name || c.ref;
            return (
              // next/link 대신 일반 <a> — /live/{ref}는 로그인 여부에 따라 redirect()를 던지는데,
              // Next 16 dev의 RSC 성능계측 코드가 클라이언트 소프트내비 중 그 redirect를 "컴포넌트
              // 에러"로 오인해 잘못된 시간값으로 performance.measure()를 호출하다 크래시한다(Next
              // 내부 버그). 일반 링크로 완전한 브라우저 이동을 하면 이 계측 경로를 타지 않는다.
              <a
                key={c.ref}
                href={c.live_active ? `/live/${c.ref}` : `/${c.ref}`}
                className="block rounded-xl overflow-hidden border border-gray-200 hover:opacity-90"
              >
                {thumb ? (
                  <div className="relative w-full aspect-[9/16] bg-gray-100 overflow-hidden">
                    <img
                      src={thumb}
                      alt={label}
                      className="absolute inset-0 block w-full h-full object-cover"
                      style={{ objectFit: 'cover', objectPosition: 'center', transform: 'scale(1.18)' }}
                      loading="lazy"
                      onError={e => {
                        // 진짜 로드 실패(네트워크 오류 등)면 여기로 온다 — mqdefault로 1회 폴백.
                        const fallback = buildYoutubeThumbnailFallback(c.live_url ?? '');
                        if (fallback && e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                      }}
                      onLoad={e => {
                        // 유튜브는 maxresdefault가 없는 영상이면 HTTP 404에 120x90 회색 "썸네일 없음"
                        // placeholder 이미지를 함께 내려준다 — 유효한 이미지라 onError는 발동하지
                        // 않으므로, 로드된 실제 크기로 판별해서 mqdefault로 교체한다.
                        const img = e.currentTarget;
                        if (img.naturalWidth > 0 && img.naturalWidth <= 120) {
                          const fallback = buildYoutubeThumbnailFallback(c.live_url ?? '');
                          if (fallback && img.src !== fallback) img.src = fallback;
                        }
                      }}
                    />
                    <div className="absolute inset-x-0 top-0 p-1.5 flex justify-start gap-1">
                      <span className="inline-flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />LIVE
                      </span>
                      {!!c.adult_only && (
                        <span className="inline-flex items-center bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">19</span>
                      )}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                      <p className="text-white text-xs font-semibold line-clamp-2">{label}</p>
                      <div className="flex items-end justify-between mt-0.5 gap-1">
                        <p className="text-white/70 text-[11px] truncate">@{c.nickname || c.ref}</p>
                        {c.category1 && (
                          <span className="shrink-0 text-white text-[10px] bg-white/25 backdrop-blur-sm rounded px-1.5 py-0.5">
                            {c.category1}{c.category2 ? `·${c.category2}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : c.channel_image ? (
                  <div className="relative w-full aspect-[9/16] bg-gray-100 overflow-hidden">
                    <img
                      src={c.channel_image}
                      alt={label}
                      className="absolute inset-0 block w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                      <p className="text-white text-xs font-semibold line-clamp-2">{label}</p>
                      <div className="flex items-end justify-between mt-0.5 gap-1">
                        <p className="text-white/70 text-[11px] truncate">@{c.nickname || c.ref}</p>
                        {c.category1 && (
                          <span className="shrink-0 text-white text-[10px] bg-white/25 backdrop-blur-sm rounded px-1.5 py-0.5">
                            {c.category1}{c.category2 ? `·${c.category2}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`relative w-full aspect-[9/16] ${pickCardColor(c.ref)} flex items-center justify-center p-3`}>
                    <p className="text-gray-800 text-sm font-semibold text-center line-clamp-3">{label}</p>
                    <div className="absolute inset-x-0 bottom-0 p-2 flex items-end justify-between gap-1">
                      <p className="text-gray-700 text-[11px] truncate">@{c.nickname || c.ref}</p>
                      {c.category1 && (
                        <span className="shrink-0 text-gray-700 text-[10px] bg-white/70 backdrop-blur-sm rounded px-1.5 py-0.5">
                          {c.category1}{c.category2 ? `·${c.category2}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
